import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Star, MapPin, Phone, Mail, Clock, ArrowRight } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

/* ═══════════════════════════════════════════════════════════════
   🎨 PALET WARNA KAYU ELEGAN — digunakan di seluruh About & Contact
   Warm teak: #5C3D1E (coklat tua kayu)
   Mid wood : #8B5E3C (kayu sedang)
   Light wood: #C4956A (kayu muda/honey)
   Cream     : #F7F0E6 (krem hangat)
   Gold accent: #B8873F (emas tua)
═══════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════
   🖼️  KONFIGURASI FOTO — EDIT DI SINI SAJA
   Cukup ganti nilai "src" dengan path foto kamu, contoh:
   '/images/namafile.jpg'

   Ada 3 spot foto di halaman About:
   1) ORIGIN_PHOTO        → 1 foto besar (tampak depan / gerbang)
   2) HIDDEN_PARADISE_PHOTOS → 4 foto kecil (kolam, taman, kamar, restoran)
   3) IJEN_PHOTO          → 1 foto besar (Kawah Ijen)
══════════════════════════════════════════════════════════════ */

// 1) SECTION "ASAL MULA" — foto tampak depan / gerbang / bangunan utama (rasio potret 4:5)
const ORIGIN_PHOTO = '/images/VIEW HALAMAN (17).JPG';

// 2) SECTION "SURGA TERSEMBUNYI" — 4 foto (rasio kotak 1:1)
//    Urutan bebas, cukup ganti src & label sesuai foto kamu.
const HIDDEN_PARADISE_PHOTOS = [
  { src: '/images/KOLAM RENANG 1.jpg',     label: { id: 'Kolam Renang',  en: 'Swimming Pool',   zh: '游泳池'   } },
  { src: '/images/VIEW HALAMAN (16).JPG',  label: { id: 'Taman Asri',    en: 'Lush Garden',     zh: '绿意庭院' } },
  { src: '/images/SRITANJUNG (1).jpg',     label: { id: 'Kamar Mewah',   en: 'Elegant Room',    zh: '豪华客房' } },
  { src: '/images/tempat makan1.jpg',label: { id: 'Area Restoran', en: 'Restaurant Area', zh: '餐饮区'   } },
];

// 3) SECTION "PINTU MENUJU IJEN" — foto Kawah Ijen (rasio landscape 4:3)
const IJEN_PHOTO = '/images/kawahijen3.JPG';
/* ══════════════════════════════════════════════════════════════ */


/* ─── SHARED HERO (FAQ / Privacy / Terms) ──────────────────── */
function PageHero({ tag, title, subtitle }) {
  return (
    <div className="pt-32 pb-16 px-6 relative overflow-hidden" style={{ backgroundColor: '#2C1A0E' }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23C4956A' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")`
      }} />
      <div className="relative max-w-4xl mx-auto text-center z-10">
        <p className="text-xs font-sans tracking-[0.3em] uppercase mb-3" style={{ color: '#C4956A' }}>— {tag} —</p>
        <h1 className="font-display text-5xl text-white font-bold mb-4">{title}</h1>
        {subtitle && <p className="font-body text-lg max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{subtitle}</p>}
      </div>
    </div>
  );
}

/* ─── FAQ PAGE ─────────────────────────────────────────────── */
const FAQ_ID = [
  { category: 'Reservasi & Booking', items: [
    { q: 'Bagaimana cara memesan kamar di Ndalem Mataram?', a: 'Kamu bisa memesan langsung melalui website ini di halaman Kamar Kami, pilih kamar yang diinginkan, pilih tanggal check-in dan check-out, lalu ikuti proses pemesanan. Kami juga menerima reservasi via WhatsApp.' },
    { q: 'Berapa lama sebelum menginap saya harus melakukan pemesanan?', a: 'Tidak ada batasan waktu minimum, namun kami menyarankan pemesanan minimal 1-3 hari sebelumnya untuk memastikan ketersediaan kamar. Untuk long weekend atau hari libur nasional, disarankan pesan 1-2 minggu sebelumnya.' },
    { q: 'Apakah ada biaya pembatalan?', a: 'Pembatalan lebih dari 3 hari sebelum check-in tidak dikenakan biaya. Pembatalan 1-3 hari sebelum check-in dikenakan biaya 50% dari total harga. Tidak ada refund untuk pembatalan kurang dari 24 jam atau no-show.' },
    { q: 'Bisakah saya mengubah tanggal reservasi?', a: 'Ya, perubahan tanggal bisa dilakukan maksimal 2 hari sebelum jadwal check-in awal, tergantung ketersediaan kamar. Hubungi kami via WhatsApp atau email untuk perubahan.' },
    { q: 'Apakah ada deposit atau uang muka?', a: 'Untuk booking langsung di website, pembayaran dilakukan sepenuhnya saat pemesanan. Untuk booking via WhatsApp, kami meminta deposit 50% untuk mengkonfirmasi reservasi.' },
  ]},
  { category: 'Check-in & Check-out', items: [
    { q: 'Jam berapa check-in dan check-out?', a: 'Check-in: 14:00 WIB. Check-out: 12:00 WIB. Early check-in dan late check-out bisa diatur sesuai ketersediaan, silakan hubungi kami sebelumnya.' },
    { q: 'Apakah tersedia resepsionis 24 jam?', a: 'Ya, kami memiliki resepsionis yang siap melayani selama 24 jam. Untuk kebutuhan mendadak di luar jam kerja, tamu bisa menghubungi nomor darurat.' },
    { q: 'Dokumen apa yang perlu dibawa saat check-in?', a: 'Tamu perlu membawa identitas diri yang valid (KTP/Paspor) dan bukti pemesanan (email konfirmasi atau screenshot). Untuk tamu asing, diperlukan paspor yang masih berlaku.' },
  ]},
  { category: 'Fasilitas & Kamar', items: [
    { q: 'Apakah semua kamar sudah termasuk sarapan?', a: 'Sarapan lokal Jawa tersedia setiap hari pukul 07:00–10:00. Untuk kamar tertentu, sarapan sudah termasuk dalam paket. Silakan cek detail kamar untuk informasi lebih lanjut.' },
    { q: 'Apakah ada kolam renang?', a: 'Ya! Kami memiliki kolam renang outdoor yang bisa dinikmati semua tamu. Kolam renang buka setiap hari pukul 06:00–21:00.' },
    { q: 'Apakah tersedia WiFi gratis?', a: 'Ya, WiFi gratis berkecepatan tinggi tersedia di seluruh area penginapan termasuk kamar dan area umum.' },
  ]},
];
const FAQ_EN = [
  { category: 'Reservation & Booking', items: [
    { q: 'How do I book a room at Ndalem Mataram?', a: 'You can book directly through our website on the Our Rooms page, select your desired room and dates, then follow the booking process. We also accept reservations via WhatsApp.' },
    { q: 'How far in advance should I book?', a: "There's no minimum booking window, but we recommend booking 1-3 days in advance to ensure availability. For long weekends and national holidays, we suggest booking 1-2 weeks ahead." },
    { q: 'What is the cancellation policy?', a: 'Cancellations more than 3 days before check-in are free. Cancellations 1-3 days before check-in incur a 50% fee. No refunds for cancellations less than 24 hours before check-in or no-shows.' },
    { q: 'Can I change my reservation dates?', a: 'Yes, date changes can be made up to 2 days before the original check-in date, subject to availability. Contact us via WhatsApp or email for changes.' },
    { q: 'Is a deposit required?', a: 'For online bookings, full payment is made at the time of reservation. For WhatsApp bookings, we require a 50% deposit to confirm your reservation.' },
  ]},
  { category: 'Check-in & Check-out', items: [
    { q: 'What are the check-in and check-out times?', a: 'Check-in: 2:00 PM. Check-out: 12:00 PM. Early check-in and late check-out may be available upon request — please contact us in advance.' },
    { q: 'Is there 24-hour reception?', a: 'Yes, our reception is available 24 hours a day. For urgent matters outside regular hours, guests can use the emergency contact number.' },
    { q: 'What documents are needed at check-in?', a: 'Guests need a valid ID (National ID/Passport) and booking confirmation (email or screenshot). Foreign guests must present a valid passport.' },
  ]},
  { category: 'Facilities & Rooms', items: [
    { q: 'Does the room rate include breakfast?', a: 'Local Javanese breakfast is available daily from 7:00 AM–10:00 AM. Some room packages include breakfast — please check each room listing for details.' },
    { q: 'Is there a swimming pool?', a: 'Yes! We have an outdoor swimming pool available to all guests, open daily from 6:00 AM–9:00 PM.' },
    { q: 'Is free WiFi available?', a: 'Yes, free high-speed WiFi is available throughout the property including rooms and common areas.' },
  ]},
];
const FAQ_ZH = [
  { category: '预订与预订', items: [
    { q: '如何在 Ndalem Mataram 预订客房？', a: '您可以直接通过我们网站的"我们的客房"页面进行预订，选择您想要的客房和日期，然后按照预订流程操作。我们也接受通过 WhatsApp 预订。' },
    { q: '我应该提前多久预订？', a: '没有最短预订时间限制，但我们建议提前1-3天预订以确保有房。对于长周末和国家法定假日，我们建议提前1-2周预订。' },
    { q: '取消政策是什么？', a: '入住前3天以上取消免费。入住前1-3天取消将收取总价的50%。入住前不足24小时取消或未入住不予退款。' },
    { q: '我可以更改预订日期吗？', a: '可以，视客房供应情况而定，最多可在原定入住日期前2天更改日期。请通过 WhatsApp 或电子邮件联系我们进行更改。' },
    { q: '需要支付押金吗？', a: '对于在线预订，需在预订时全额付款。对于 WhatsApp 预订，我们需要50%的押金以确认您的预订。' },
  ]},
  { category: '入住与退房', items: [
    { q: '入住和退房时间是几点？', a: '入住时间：下午 2:00。退房时间：中午 12:00。可应要求提供提前入住和延迟退房服务——请提前联系我们。' },
    { q: '有24小时前台吗？', a: '是的，我们的前台全天24小时提供服务。对于正常工作时间以外的紧急情况，客人可以使用房间内提供的紧急联系电话。' },
    { q: '办理入住需要什么文件？', a: '客人需要提供有效的身份证件（身份证/护照）和预订确认信（电子邮件或截图）。外国客人必须出示有效护照。' },
  ]},
  { category: '设施与客房', items: [
    { q: '房价包含早餐吗？', a: '每天上午 7:00–10:00 提供当地爪哇早餐。部分客房套餐包含早餐——请查看每个客房的详细信息。' },
    { q: '有游泳池吗？', a: '是的！我们有一个室外游泳池供所有客人使用，每天早上 6:00–晚上 9:00 开放。' },
    { q: '提供免费 WiFi 吗？', a: '是的，酒店各处（包括客房和公共区域）均提供免费高速 WiFi。' },
  ]},
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-xl overflow-hidden" style={{ borderColor: '#E8D5B7' }}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white transition-colors"
        style={{ '--hover-bg': '#FDF6EC' }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FDF6EC'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
        <span className="font-sans font-semibold text-sm pr-4" style={{ color: '#2C1A0E' }}>{q}</span>
        {open
          ? <ChevronUp size={18} style={{ color: '#8B5E3C' }} className="flex-shrink-0" />
          : <ChevronDown size={18} style={{ color: '#8B5E3C' }} className="flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 py-4 border-t" style={{ backgroundColor: '#FDF6EC', borderColor: '#E8D5B7' }}>
          <p className="font-sans text-sm leading-relaxed" style={{ color: '#5C3D1E' }}>{a}</p>
        </div>
      )}
    </div>
  );
}

export function FAQPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const FAQ = lang === 'zh' ? FAQ_ZH : lang === 'en' ? FAQ_EN : FAQ_ID;
  usePageTitle(lang === 'id' ? 'FAQ – Ndalem Mataram' : lang === 'zh' ? '常见问题解答 – Ndalem Mataram' : 'FAQ – Ndalem Mataram');
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F0E6' }}>
      <PageHero
        tag={lang === 'id' ? 'Pertanyaan Umum' : lang === 'zh' ? '常见问题' : 'Common Questions'}
        title="FAQ"
        subtitle={lang === 'id' ? 'Temukan jawaban atas pertanyaan yang sering diajukan tamu kami' : lang === 'zh' ? '找到客人经常提出的问题的答案' : 'Find answers to questions our guests frequently ask'} />
      <div className="max-w-3xl mx-auto px-6 py-16 space-y-10">
        {FAQ.map((section) => (
          <div key={section.category}>
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2" style={{ color: '#2C1A0E' }}>
              <span className="w-1 h-6 rounded-full inline-block" style={{ backgroundColor: '#B8873F' }} />
              {section.category}
            </h2>
            <div className="space-y-2">
              {section.items.map((item, i) => <FAQItem key={i} q={item.q} a={item.a} />)}
            </div>
          </div>
        ))}
        <div className="rounded-2xl p-8 text-center mt-10" style={{ backgroundColor: '#2C1A0E' }}>
          <p className="font-sans text-sm mb-3" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {lang === 'id' ? 'Tidak menemukan jawaban yang kamu cari?' : lang === 'zh' ? '没有找到您要找的答案？' : "Didn't find the answer you're looking for?"}
          </p>
          <a href="https://wa.me/6281378575758" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 text-white font-sans font-semibold rounded-xl transition-colors"
            style={{ backgroundColor: '#25D366' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#128C7E'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#25D366'}>
            💬 {lang === 'id' ? 'Tanya via WhatsApp' : lang === 'zh' ? '通过 WhatsApp 提问' : 'Ask via WhatsApp'}
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── PRIVACY PAGE ──────────────────────────────────────────── */
export function PrivacyPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const sections_id = [
    { title: '1. Informasi yang Kami Kumpulkan', body: 'Kami mengumpulkan informasi yang Anda berikan secara langsung saat melakukan pemesanan, yaitu: nama lengkap, alamat email, nomor telepon, dan informasi kartu identitas yang diperlukan untuk check-in. Kami juga mengumpulkan data teknis untuk keperluan analitik.' },
    { title: '2. Penggunaan Informasi', body: 'Informasi yang Anda berikan digunakan untuk: memproses reservasi Anda, mengirimkan informasi terkait menginap, merespons pertanyaan, dan meningkatkan kualitas layanan kami.' },
    { title: '3. Keamanan Data', body: 'Kami berkomitmen untuk melindungi data pribadi Anda. Kami tidak menyimpan data kartu kredit/debit Anda di server kami. Akses ke data tamu dibatasi hanya untuk staf yang berwenang.' },
    { title: '4. Berbagi Informasi', body: 'Kami tidak menjual atau membagikan informasi pribadi Anda kepada pihak ketiga untuk pemasaran. Informasi hanya dibagikan kepada pihak ketiga yang terlibat langsung dalam layanan Anda seperlunya.' },
    { title: '5. Cookie', body: 'Website kami menggunakan cookie untuk menyimpan preferensi bahasa Anda. Anda dapat menonaktifkan cookie melalui pengaturan browser.' },
    { title: '6. Kontak', body: 'Untuk pertanyaan terkait privasi data Anda, hubungi kami di: ndalem.mataram1@gmail.com atau WhatsApp +62 813-7857-5758.' },
  ];
  const sections_en = [
    { title: '1. Information We Collect', body: 'We collect information you provide directly when making a reservation: full name, email address, phone number, and identity information required for check-in. We also collect technical data for analytics.' },
    { title: '2. Use of Information', body: 'Your information is used to: process your reservation, send stay-related communications, respond to inquiries, and improve our service quality.' },
    { title: '3. Data Security', body: 'We are committed to protecting your personal data. We do not store your credit/debit card data on our servers. Access to guest data is restricted to authorized staff only.' },
    { title: '4. Information Sharing', body: 'We do not sell or share your personal information with third parties for marketing. Information is only shared with third parties directly involved in your service as necessary.' },
    { title: '5. Cookies', body: 'Our website uses cookies to store your language preferences. You can disable cookies through your browser settings.' },
    { title: '6. Contact', body: 'For questions about your data privacy, contact us at: ndalem.mataram1@gmail.com or WhatsApp +62 813-7857-5758.' },
  ];
  const sections_zh = [
    { title: '1. 我们收集的信息', body: '我们在您预订时收集您直接提供的信息：全名、电子邮件地址、电话号码以及入住所需的身份信息。我们还收集技术数据用于分析。' },
    { title: '2. 信息的使用', body: '您的信息用于：处理您的预订、发送与入住相关的通信、回复查询以及提高我们的服务质量。' },
    { title: '3. 数据安全', body: '我们致力于保护您的个人数据。我们不会在服务器上存储您的信用卡/借记卡数据。访客数据的访问权限仅限于授权员工。' },
    { title: '4. 信息共享', body: '我们不会出于营销目的向第三方出售或共享您的个人信息。信息仅在必要时与直接参与您服务的第三方共享。' },
    { title: '5. Cookies', body: '我们的网站使用 cookies 来存储您的语言偏好。您可以通过浏览器设置禁用 cookies。' },
    { title: '6. 联系方式', body: '有关数据隐私的问题，请联系：ndalem.mataram1@gmail.com 或 WhatsApp +62 813-7857-5758。' },
  ];
  const sections = lang === 'zh' ? sections_zh : lang === 'en' ? sections_en : sections_id;
  usePageTitle(lang === 'id' ? 'Kebijakan Privasi – Ndalem Mataram' : lang === 'zh' ? '隐私政策 – Ndalem Mataram' : 'Privacy Policy – Ndalem Mataram');
  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#F7F0E6' }}>
      <PageHero tag={lang === 'id' ? 'Kebijakan' : lang === 'zh' ? '政策' : 'Policy'}
        title={lang === 'id' ? 'Privasi & Disclaimer' : lang === 'zh' ? '隐私与免责声明' : 'Privacy & Disclaimer'}
        subtitle={lang === 'id' ? 'Terakhir diperbarui: Maret 2026' : lang === 'zh' ? '最后更新：2026年3月' : 'Last updated: March 2026'} />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl shadow-sm divide-y" style={{ border: '1px solid #E8D5B7', borderColor: '#E8D5B7' }}>
          {sections.map((s, i) => (
            <div key={i} className="p-6 transition-colors hover:bg-stone-50">
              <h2 className="font-sans font-bold mb-3" style={{ color: '#2C1A0E' }}>{s.title}</h2>
              <p className="font-sans text-sm leading-relaxed" style={{ color: '#5C3D1E' }}>{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/" className="font-sans text-sm font-semibold hover:underline" style={{ color: '#8B5E3C' }}>
            ← {lang === 'id' ? 'Kembali ke Beranda' : lang === 'zh' ? '返回首页' : 'Back to Home'}
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─── TERMS PAGE ────────────────────────────────────────────── */
export function TermsPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const sections_id = [
    { title: '1. Penerimaan Syarat', body: 'Dengan melakukan pemesanan, Anda menyetujui syarat dan ketentuan yang berlaku. Kami berhak mengubah syarat ini sewaktu-waktu.' },
    { title: '2. Pemesanan & Pembayaran', body: 'Semua pemesanan dianggap sah setelah menerima konfirmasi dari kami. Harga yang tertera sudah termasuk pajak kecuali disebutkan lain.' },
    { title: '3. Kebijakan Pembatalan', body: 'Pembatalan lebih dari 3 hari sebelum check-in: gratis. Pembatalan 1–3 hari sebelum check-in: biaya 50%. Kurang dari 24 jam: tidak ada pengembalian dana.' },
    { title: '4. Peraturan Tamu', body: 'Dilarang membawa tamu tidak menginap ke area kamar tanpa izin. Merokok hanya di area yang ditentukan. Hewan peliharaan tidak diperbolehkan.' },
    { title: '5. Tanggung Jawab', body: 'Kami tidak bertanggung jawab atas kehilangan barang pribadi tamu. Tamu bertanggung jawab atas kerusakan fasilitas yang disebabkannya.' },
    { title: '6. Hukum yang Berlaku', body: 'Syarat dan ketentuan ini tunduk pada hukum Republik Indonesia di pengadilan yang berwenang di Banyuwangi.' },
  ];
  const sections_en = [
    { title: '1. Acceptance of Terms', body: 'By making a reservation, you agree to these terms and conditions. We reserve the right to modify these terms at any time.' },
    { title: '2. Reservations & Payment', body: 'All reservations are valid upon receiving confirmation. Prices shown include taxes unless otherwise stated.' },
    { title: '3. Cancellation Policy', body: 'Cancellation >3 days before check-in: free. 1–3 days before check-in: 50% fee. Less than 24 hours: no refund.' },
    { title: '4. Guest Rules', body: 'Unregistered visitors are not permitted in room areas without authorization. Smoking is only allowed in designated areas. Pets are not permitted.' },
    { title: '5. Liability', body: 'We are not responsible for loss to personal belongings. Guests are responsible for any damage to facilities they cause.' },
    { title: '6. Governing Law', body: 'These terms are governed by the laws of the Republic of Indonesia through the competent court in Banyuwangi.' },
  ];
  const sections_zh = [
    { title: '1. 接受条款', body: '进行预订即表示您同意这些条款和条件。我们保留随时修改这些条款的权利。' },
    { title: '2. 预订与付款', body: '所有预订在收到确认后生效。除非另有说明，所显示的价格均含税。' },
    { title: '3. 取消政策', body: '入住前3天以上取消：免费。入住前1-3天取消：收取50%费用。不足24小时：不予退款。' },
    { title: '4. 客人守则', body: '未经授权，未登记访客不得进入客房区域。仅允许在指定区域吸烟。禁止携带宠物。' },
    { title: '5. 责任', body: '我们对个人物品的遗失概不负责。客人需对造成的任何设施损坏负责。' },
    { title: '6. 适用法律', body: '这些条款受印度尼西亚共和国法律管辖，由外南梦 (Banyuwangi) 的主管法院管辖。' },
  ];
  const sections = lang === 'zh' ? sections_zh : lang === 'en' ? sections_en : sections_id;
  usePageTitle(lang === 'id' ? 'Syarat & Ketentuan – Ndalem Mataram' : lang === 'zh' ? '条款与条件 – Ndalem Mataram' : 'Terms & Conditions – Ndalem Mataram');
  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#F7F0E6' }}>
      <PageHero tag={lang === 'id' ? 'Ketentuan' : lang === 'zh' ? '法律' : 'Legal'}
        title={lang === 'id' ? 'Syarat & Ketentuan' : lang === 'zh' ? '条款与条件' : 'Terms & Conditions'}
        subtitle={lang === 'id' ? 'Terakhir diperbarui: Maret 2026' : lang === 'zh' ? '最后更新：2026年3月' : 'Last updated: March 2026'} />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl shadow-sm divide-y" style={{ border: '1px solid #E8D5B7' }}>
          {sections.map((s, i) => (
            <div key={i} className="p-6 transition-colors hover:bg-stone-50">
              <h2 className="font-sans font-bold mb-3" style={{ color: '#2C1A0E' }}>{s.title}</h2>
              <p className="font-sans text-sm leading-relaxed" style={{ color: '#5C3D1E' }}>{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/" className="font-sans text-sm font-semibold hover:underline" style={{ color: '#8B5E3C' }}>
            ← {lang === 'id' ? 'Kembali ke Beranda' : lang === 'zh' ? '返回首页' : 'Back to Home'}
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ABOUT PAGE
   Palet: #2C1A0E (teak gelap) · #5C3D1E (kayu mid) · #8B5E3C (honey)
          #C4956A (kayu muda) · #B8873F (emas tua) · #F7F0E6 (krem)
═══════════════════════════════════════════════════════════════ */

function WoodDivider() {
  return (
    <div className="flex items-center justify-center gap-3 my-2">
      <span className="w-10 h-px" style={{ backgroundColor: '#C4956A', opacity: 0.5 }} />
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#B8873F' }} />
      <span className="w-10 h-px" style={{ backgroundColor: '#C4956A', opacity: 0.5 }} />
    </div>
  );
}

function WoodPullQuote({ text }) {
  return (
    <blockquote className="relative pl-6 italic font-sans text-base leading-relaxed my-8"
      style={{ borderLeft: '4px solid #B8873F', color: '#8B5E3C' }}>
      <span className="absolute -top-3 -left-3 text-5xl font-serif leading-none select-none" style={{ color: '#E8D5B7' }}>"</span>
      {text}
    </blockquote>
  );
}

function StatPill({ number, label }) {
  return (
    <div className="text-center">
      <p className="font-display text-5xl font-bold leading-none mb-1" style={{ color: '#C4956A' }}>{number}</p>
      <p className="font-sans text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
    </div>
  );
}

// 🧱 ValueCard — versi tanpa ikon, langsung ke judul (aksen garis kayu kecil di atas judul)
function ValueCard({ title, desc }) {
  return (
    <div className="group rounded-3xl p-8 transition-all duration-500 hover:-translate-y-1 cursor-default"
      style={{ backgroundColor: 'rgba(196,149,106,0.08)', border: '1px solid rgba(196,149,106,0.2)' }}
      onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(196,149,106,0.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(196,149,106,0.08)'; }}>
      <span className="block w-10 h-[3px] rounded-full mb-5 transition-all duration-300 group-hover:w-16"
        style={{ backgroundColor: '#B8873F' }} />
      <h3 className="font-display text-white font-bold text-lg mb-3 leading-snug">{title}</h3>
      <p className="font-sans text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{desc}</p>
    </div>
  );
}

export function AboutPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  usePageTitle(lang === 'id' ? 'Tentang Kami – Ndalem Mataram' : lang === 'zh' ? '关于我们 – Ndalem Mataram' : 'About Us – Ndalem Mataram');

  const t = (obj) => obj[lang] || obj['id'];

  /* ── COPY tiga bahasa ── */
  const copy = {
    heroTag:      { id: 'Cerita Kami',               en: 'Our Story',                   zh: '我们的故事'             },
    heroTitle:    { id: 'Tentang Ndalem Mataram',     en: 'About Ndalem Mataram',         zh: '关于 Ndalem Mataram'    },
    heroSub:      {
      id: 'Di ujung timur Pulau Jawa, di kota yang menyimpan legenda dan bau bunga kopi, ada sebuah rumah yang menunggu.',
      en: 'At the eastern tip of Java, in a city of legends and coffee-blossom air, there is a house that awaits.',
      zh: '在爪哇岛的最东端，在一座充满传说与咖啡花香的城市里，有一座静静等候的家。',
    },
    origin_tag:   { id: 'Asal Mula',                 en: 'The Origin',                  zh: '起源'                   },
    origin_h2:    { id: 'Sebuah Nama yang Berarti Pulang', en: 'A Name That Means Coming Home', zh: '一个意味着回家的名字' },
    origin_p1:    {
      id: '"Ndalem" dalam bahasa Jawa bukan sekadar kata untuk rumah. Ia adalah ruang di mana debu perjalanan luruh, di mana teh diseduh tanpa diminta, di mana tuan rumah mengenal nama tamu sebelum check-out. Itulah jiwa yang kami jaga sejak hari pertama pintu ini dibuka.',
      en: '"Ndalem" in Javanese is not simply the word for house. It is the space where travel dust dissolves, where tea is brewed before you ask, where hosts know your name before you check out. That is the soul we have kept since the very first day this door opened.',
      zh: '"Ndalem"这个爪哇语词汇不只是"家"的意思。它是旅途尘埃消散的空间，是在你开口之前便已沏好的茶，是主人在退房之前便已知晓你名字的地方。这是我们自开业第一天起便珍视的灵魂。',
    },
    origin_p2:    {
      id: 'Kami lahir di Banyuwangi — kota yang disebut "The Sunrise of Java", tanah leluhur suku Osing, pewaris kebudayaan Blambangan yang berusia berabad-abad. Legenda Sri Tanjung, Tari Gandrung, aroma kopi Ijen yang mengepul di pagi hari — semua itu ada di udara yang sama yang tamu kami hirup saat berjalan di taman properti ini.',
      en: 'We were born in Banyuwangi — the city called "The Sunrise of Java", homeland of the Osing people, heirs to the centuries-old Blambangan civilization. The legend of Sri Tanjung, the Gandrung dance, the morning scent of Ijen coffee — all of it breathed in the same sky our guests walk beneath in this garden.',
      zh: '我们诞生于外南梦——这座被称为"爪哇的日出之地"的城市，是奥辛族的故乡，布拉姆班甘数百年文明的传承者。斯里丹珠的传说、甘德隆舞蹈、清晨宜珍咖啡飘散的香气——这一切都弥漫在宾客漫步于我们花园时所呼吸的同一片天空之下。',
    },
    origin_quote: {
      id: 'Di balik dinding tinggi dan gerbang kayu ukir itu, ditemukan sebuah surga kecil di tengah kota.',
      en: 'Behind the tall walls and carved wooden gate, a hidden paradise in the heart of the city.',
      zh: '在高墙与雕花木门之后，是藏匿于城市心脏的一片小天地。',
    },
    place_tag:    { id: 'Tentang Tempat Ini',         en: 'The Place',                   zh: '关于这里'               },
    place_h2:     { id: 'Surga Tersembunyi yang Bisa Kamu Temukan', en: 'A Hidden Paradise You Can Find', zh: '触手可及的隐世秘境' },
    place_p1:     {
      id: 'Tamu kami sering berkata hal yang sama: mereka tidak menyangka tempat seperti ini ada di sini. Di balik pagar bata merah di Jl. K.H. Agus Salim — jarak berjalan dari pusat kota, pasar kuliner Osing, dan stasiun kereta — tersembunyi taman yang rimbun, kolam renang jernih, dan deretan bungalow berarsitektur Jawa klasik.',
      en: 'Our guests often say the same thing: they did not expect a place like this to exist here. Behind the red-brick fence on Jl. K.H. Agus Salim — walking distance from the city center, the Osing culinary market, and the train station — lies a lush garden, a clear swimming pool, and bungalows in classic Javanese architecture.',
      zh: '宾客们常说同一句话：他们没想到这里居然有这样的地方。在Jl. K.H. Agus Salim的红砖围墙之后——步行可达市中心、奥辛美食市场和火车站——隐藏着郁郁葱葱的花园、清澈的游泳池，以及经典爪哇风格的小屋。',
    },
    place_p2:     {
      id: 'Setiap kamar punya nama yang berakar pada tempat-tempat bersejarah di Banyuwangi: Sri Tanjung, Kawah Ijen, Sukamade, Bangsring, Minakjinggo. Bukan sekadar nama dekorasi — melainkan undangan untuk mengenal lebih dalam bumi yang ditapak tamu selama menginap.',
      en: 'Every room carries a name rooted in the historical places of Banyuwangi: Sri Tanjung, Kawah Ijen, Sukamade, Bangsring, Minakjinggo. Not merely decorative names — but invitations to know more deeply the land guests walk upon during their stay.',
      zh: '每间客房都以外南梦的历史地名命名：斯里丹珠、宜珍火山、苏卡玛德、班斯令、米纳基庄戈。这些不只是装饰性的名称——而是邀请宾客更深入了解这片土地的请柬。',
    },
    ijen_tag:     { id: 'Pintu Menuju Ijen',          en: 'Gateway to Ijen',             zh: '通往宜珍之门'           },
    ijen_h2:      { id: 'Basecamp Terbaik untuk Petualangan Ijen', en: 'The Best Basecamp for Your Ijen Adventure', zh: '探索宜珍火山的最佳大本营' },
    ijen_p1:      {
      id: 'Kawah Ijen adalah salah satu fenomena alam paling dramatis di bumi — danau kawah berwarna toska di ketinggian 2.386 m, dikelilingi api biru yang hanya ada di dua tempat di dunia. Para pendaki biasanya tiba di Banyuwangi malam hari, berangkat tengah malam, dan kembali fajar.',
      en: 'Kawah Ijen is one of the most dramatic natural phenomena on earth — a turquoise crater lake at 2,386 m elevation, surrounded by blue fire found in only two places on the planet. Hikers typically arrive in Banyuwangi at night, depart at midnight, and return at dawn.',
      zh: '宜珍火山口是地球上最震撼的自然奇观之一——位于海拔2386米处的青绿色火山湖，四周环绕着全球仅两处可见的蓝色火焰。登山者通常在夜间抵达外南梦，午夜出发，黎明前返回。',
    },
    ijen_p2:      {
      id: 'Kami sudah terbiasa dengan ritme itu. Tim kami bisa menyiapkan sarapan pagi buta, memanggil transportasi, dan memastikan tamu pulang dari puncak langsung menuju kolam renang atau kasur bersih yang sudah menunggu. Itu bukan layanan tambahan. Itu hal yang kami lakukan setiap hari.',
      en: 'We know that rhythm well. Our team can prepare breakfast before dawn, arrange transport, and ensure guests return from the summit straight to a pool or a clean bed already waiting. That is not an added service. That is simply what we do every day.',
      zh: '我们对这种节奏早已熟悉。我们的团队可以在黎明前备好早餐、安排交通，确保宾客从山顶归来后直接迎接已经等候的泳池或洁净的床铺。这不是附加服务，这只是我们每天所做的事。',
    },
    values_tag:   { id: 'Nilai Kami',                en: 'Our Values',                  zh: '我们的价值观'           },
    values_h2:    { id: 'Empat Hal yang Kami Pegang Teguh', en: 'Four Things We Stand By', zh: '我们坚守的四项价值'   },
    stats: [
      { number: '14+',  label: { id: 'Kamar & Bungalow',         en: 'Rooms & Bungalows',     zh: '客房与小屋'   } },
      { number: '24/7', label: { id: 'Layanan Resepsionis',       en: 'Front Desk Service',    zh: '前台服务'     } },
      { number: '∞',    label: { id: 'Cerita yang Dibawa Pulang', en: 'Stories Taken Home',    zh: '带回家的故事' } },
    ],
    cta_h3:       { id: 'Sudah tahu ingin merasakan apa?',   en: 'Already know what you want to feel?', zh: '已经知道您想要什么体验了吗？' },
    cta_rooms:    { id: 'Pilih Kamar',     en: 'Choose a Room',  zh: '选择客房' },
    cta_contact:  { id: 'Hubungi Kami',   en: 'Contact Us',      zh: '联系我们' },
  };

  // 🧱 4 Value — sudah tanpa ikon (lihat ValueCard di atas)
  const VALUES = [
    {
      title: { id: 'Keramahan yang Tulus',      en: 'Genuine Hospitality',          zh: '真诚的热情待客'   },
      desc:  { id: 'Bukan hospitality dari buku teks. Tapi kebiasaan orang Jawa yang menyambut tamu bukan sebagai pelanggan, melainkan sebagai tamu di rumah sendiri.', en: 'Not textbook hospitality. But the Javanese habit of receiving guests not as customers, but as visitors in their own home.', zh: '不是教科书里的待客之道，而是爪哇人的习惯——将宾客视为自家来访的亲友，而非顾客。' },
    },
    {
      title: { id: 'Ketenangan di Tengah Kota', en: 'Calm Within the City',         zh: '闹市中的宁静'     },
      desc:  { id: 'Taman rimbun, kolam renang outdoor, dan hammock di bawah pohon kelapa — semua dalam jarak berjalan dari stasiun dan kuliner kota.', en: "Lush gardens, an outdoor pool, and a hammock under a coconut tree — all within walking distance of the station and the city's culinary scene.", zh: '郁郁葱葱的花园、室外游泳池，以及椰子树下的吊床——步行即可抵达车站和城市美食街。' },
    },
    {
      title: { id: 'Ruang yang Bercerita',       en: 'Spaces That Tell Stories',     zh: '会讲述故事的空间' },
      desc:  { id: 'Ukiran Jawa di pintu kamar, lantai motif batik, lampu minyak kuno — setiap sudut adalah artefak budaya yang bisa dinikmati, bukan sekadar latar foto.', en: 'Javanese carvings on room doors, batik-motif floors, antique oil lamps — every corner is a cultural artifact to be experienced, not just a photo backdrop.', zh: '客房门上的爪哇木雕、蜡染图案的地板、古老的油灯——每个角落都是可以细细品味的文化珍品。' },
    },
    {
      title: { id: 'Titik Awal Petualangan',     en: 'The Starting Point of Adventure', zh: '冒险的起点'     },
      desc:  { id: 'Kawah Ijen, Pantai Pulau Merah, hutan Sukamade — semuanya bermula dari sini. Kami bukan hanya tempat tidur, tapi juga mitra perjalananmu.', en: 'Kawah Ijen, Pulau Merah Beach, Sukamade forest — all journeys begin here. We are not just a bed, but your travel partner.', zh: '宜珍火山口、红岛海滩、苏卡玛德森林——所有旅程都从这里出发。我们不只是一张床，更是您的旅行伙伴。' },
    },
  ];

  /* Warna kayu untuk inline styles */
  const wood = {
    dark:    '#2C1A0E',
    mid:     '#5C3D1E',
    honey:   '#8B5E3C',
    light:   '#C4956A',
    gold:    '#B8873F',
    cream:   '#F7F0E6',
    creamMid:'#EDE0CC',
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: wood.cream }}>

      {/* ── HERO ── */}
      <div className="pt-36 pb-24 px-6 relative overflow-hidden" style={{ backgroundColor: wood.dark }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23C4956A'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")`
        }} />
        <div className="absolute bottom-0 left-0 right-0 h-24" style={{
          background: `linear-gradient(to top, ${wood.dark}, transparent)`
        }} />
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <p className="text-xs font-sans tracking-[0.35em] uppercase mb-5 font-bold" style={{ color: wood.light }}>
            — {t(copy.heroTag)} —
          </p>
          <h1 className="font-display text-5xl lg:text-6xl text-white font-bold mb-6 leading-tight">
            {t(copy.heroTitle)}
          </h1>
          <p className="font-sans text-lg max-w-2xl mx-auto leading-relaxed italic" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {t(copy.heroSub)}
          </p>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          SECTION 1 — ASAL USUL
          🖼️ Foto: ganti di ORIGIN_PHOTO (paling atas file ini)
          (tampak depan properti / gerbang / bangunan utama)
      ══════════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* TEKS kiri */}
          <div>
            <p className="text-xs font-sans tracking-[0.3em] uppercase font-bold mb-4" style={{ color: wood.gold }}>
              — {t(copy.origin_tag)} —
            </p>
            <h2 className="font-display text-4xl font-bold leading-tight mb-6" style={{ color: wood.dark }}>
              {t(copy.origin_h2)}
            </h2>
            <p className="font-sans leading-relaxed text-base mb-5" style={{ color: wood.mid }}>
              {t(copy.origin_p1)}
            </p>
            <p className="font-sans leading-relaxed text-base mb-6" style={{ color: wood.mid }}>
              {t(copy.origin_p2)}
            </p>
            <WoodPullQuote text={t(copy.origin_quote)} />
          </div>

          {/* 🖼️ FOTO KANAN — Section 1 (ganti ORIGIN_PHOTO di atas)
              Ideal: foto tampak depan properti / gerbang kayu ukir / bangunan utama
              Rasio: portrait 4:5 */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl aspect-[4/5] relative"
              style={{ border: `3px solid ${wood.light}` }}>
              <img
                src={ORIGIN_PHOTO}
                alt="Ndalem Mataram"
                className="w-full h-full object-cover"
                onError={e => {
                  /* Fallback elegan jika foto belum dipasang */
                  e.target.style.display = 'none';
                  e.target.parentNode.style.backgroundColor = wood.creamMid;
                  e.target.parentNode.innerHTML = `
                    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;padding:2rem;text-align:center;">
                      <p style="font-family:serif;font-size:1.25rem;font-weight:700;color:${wood.dark}">Ndalem Mataram</p>
                      <p style="font-size:0.75rem;color:${wood.honey};margin-top:0.5rem">Foto belum dipasang</p>
                    </div>`;
                }}
              />
              {/* Overlay gradien bawah untuk teks */}
              <div className="absolute bottom-0 left-0 right-0 p-6" style={{
                background: `linear-gradient(to top, ${wood.dark}CC, transparent)`
              }}>
                <p className="text-white font-sans text-xs uppercase tracking-widest font-bold"
                  style={{ color: wood.light }}>Jl. K.H. Agus Salim No.18 · Banyuwangi</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 2 — SURGA TERSEMBUNYI
          🖼️ Grid 4 foto: ganti di HIDDEN_PARADISE_PHOTOS (paling atas file ini)
          (kolam, taman, kamar, restoran — urutan & label bebas)
      ══════════════════════════════════════════════ */}
      <section className="py-24 px-6" style={{ backgroundColor: wood.dark }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">

          {/* 🖼️ GRID 4 FOTO KIRI — Section 2 (ganti HIDDEN_PARADISE_PHOTOS di atas) */}
          <div className="grid grid-cols-2 gap-3">
            {HIDDEN_PARADISE_PHOTOS.map((item, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-square relative group"
                style={{ border: `2px solid rgba(196,149,106,0.3)` }}>
                <img
                  src={item.src}
                  alt={t(item.label)}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  onError={e => {
                    e.target.style.display = 'none';
                    e.target.parentNode.style.backgroundColor = 'rgba(196,149,106,0.12)';
                    e.target.parentNode.innerHTML = `
                      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;text-align:center;padding:1rem">
                        <p style="font-size:0.65rem;color:rgba(196,149,106,0.8);font-weight:600;text-transform:uppercase;letter-spacing:0.1em">Foto belum<br/>dipasang</p>
                      </div>`;
                  }}
                />
                {/* Label overlay */}
                <div className="absolute bottom-0 left-0 right-0 px-3 py-2" style={{
                  background: `linear-gradient(to top, ${wood.dark}CC, transparent)`
                }}>
                  <p className="font-sans text-xs font-bold uppercase tracking-wider" style={{ color: wood.light }}>
                    {t(item.label)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Teks kanan */}
          <div>
            <p className="text-xs font-sans tracking-[0.3em] uppercase font-bold mb-4" style={{ color: wood.light }}>
              — {t(copy.place_tag)} —
            </p>
            <h2 className="font-display text-4xl text-white font-bold leading-tight mb-6">
              {t(copy.place_h2)}
            </h2>
            <p className="font-sans leading-relaxed text-base mb-5" style={{ color: 'rgba(255,255,255,0.65)' }}>
              {t(copy.place_p1)}
            </p>
            <p className="font-sans leading-relaxed text-base" style={{ color: 'rgba(255,255,255,0.65)' }}>
              {t(copy.place_p2)}
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 3 — IJEN
          🖼️ Foto: ganti di IJEN_PHOTO (paling atas file ini)
          (pemandangan Kawah Ijen / api biru / panorama gunung)
      ══════════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Teks kiri */}
          <div>
            <p className="text-xs font-sans tracking-[0.3em] uppercase font-bold mb-4" style={{ color: wood.gold }}>
              — {t(copy.ijen_tag)} —
            </p>
            <h2 className="font-display text-4xl font-bold leading-tight mb-6" style={{ color: wood.dark }}>
              {t(copy.ijen_h2)}
            </h2>
            <p className="font-sans leading-relaxed text-base mb-5" style={{ color: wood.mid }}>
              {t(copy.ijen_p1)}
            </p>
            <p className="font-sans leading-relaxed text-base mb-8" style={{ color: wood.mid }}>
              {t(copy.ijen_p2)}
            </p>
            {/* Journey strip */}
            <div className="flex items-center">
              {[
                { icon: '🌙', label: { id: 'Tengah Malam', en: 'Midnight Depart', zh: '午夜出发' } },
                { icon: '🌋', label: { id: 'Puncak Ijen',  en: 'Ijen Summit',     zh: '宜珍山顶' } },
                { icon: '🌅', label: { id: 'Kembali Fajar',en: 'Dawn Return',      zh: '黎明归来' } },
                { icon: '🏊', label: { id: 'Kolam Menanti',en: 'Pool Awaits',      zh: '泳池等候' } },
              ].map((step, i, arr) => (
                <React.Fragment key={i}>
                  <div className="flex flex-col items-center text-center min-w-[68px]">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl mb-2"
                      style={{ backgroundColor: wood.creamMid, border: `2px solid ${wood.light}` }}>
                      {step.icon}
                    </div>
                    <span className="font-sans text-[10px] font-semibold uppercase tracking-wide leading-tight" style={{ color: wood.honey }}>
                      {t(step.label)}
                    </span>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="flex-1 mb-5" style={{ borderTop: `2px dashed ${wood.light}`, opacity: 0.5, marginLeft: 2, marginRight: 2 }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* 🖼️ FOTO KANAN — Section 3 (ganti IJEN_PHOTO di atas)
              Ideal: foto Kawah Ijen (api biru / danau kawah hijau / sunrise di puncak)
              Rasio: landscape 4:3 atau square */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] relative"
              style={{ border: `3px solid ${wood.light}` }}>
              <img
                src={IJEN_PHOTO}
                alt="Kawah Ijen"
                className="w-full h-full object-cover"
                onError={e => {
                  e.target.style.display = 'none';
                  e.target.parentNode.style.background = `linear-gradient(135deg, #0d1b2a, #1a3045, #0d2b45)`;
                  e.target.parentNode.innerHTML = `
                    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;text-align:center;padding:2rem">
                      <p style="font-family:serif;font-size:1.5rem;font-weight:700;color:white">Kawah Ijen</p>
                      <p style="font-size:0.75rem;color:rgba(147,197,253,0.8);font-weight:600;margin-top:0.5rem;text-transform:uppercase;letter-spacing:0.1em">Blue Fire · 2.386 m</p>
                    </div>`;
                }}
              />
              {/* Info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-5" style={{
                background: 'linear-gradient(to top, rgba(13,27,42,0.9), transparent)'
              }}>
                <p className="font-display text-white text-lg font-bold mb-0.5">Kawah Ijen</p>
                <p className="font-sans text-xs font-semibold uppercase tracking-widest" style={{ color: '#93C5FD' }}>
                  {lang === 'zh' ? '蓝色火焰 · 海拔2386米' : lang === 'en' ? 'Blue Fire · 2,386 m asl' : 'Api Biru · 2.386 m dpl'}
                </p>
              </div>
            </div>
            {/* Badge jarak */}
            <div className="absolute -bottom-4 -left-4 rounded-2xl px-4 py-3 shadow-xl"
              style={{ backgroundColor: wood.mid, border: `2px solid ${wood.light}` }}>
              <p className="font-sans text-white text-xs font-bold uppercase tracking-wider">
                ±1.5h {lang === 'zh' ? '车程' : lang === 'en' ? 'drive' : 'berkendara'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES (tanpa ikon) ── */}
      <section className="py-24 px-6" style={{ backgroundColor: wood.dark }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-sans tracking-[0.3em] uppercase mb-4 font-bold" style={{ color: wood.light }}>
              — {t(copy.values_tag)} —
            </p>
            <h2 className="font-display text-4xl text-white font-bold">{t(copy.values_h2)}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {VALUES.map((v, i) => <ValueCard key={i} title={t(v.title)} desc={t(v.desc)} />)}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6 text-center" style={{ backgroundColor: wood.creamMid, borderTop: `1px solid ${wood.light}40` }}>
        <p className="text-xs font-sans tracking-[0.3em] uppercase font-bold mb-4" style={{ color: wood.gold }}>
          — {lang === 'zh' ? '下一步' : lang === 'en' ? 'Next Step' : 'Langkah Berikutnya'} —
        </p>
        <h3 className="font-display text-4xl font-bold mb-4" style={{ color: wood.dark }}>{t(copy.cta_h3)}</h3>
        <p className="font-sans text-base mb-10 max-w-md mx-auto" style={{ color: wood.honey }}>
          {lang === 'zh' ? '预订很简单。等候您的不只是一张床，而是一段经历。'
            : lang === 'en' ? 'Booking is simple. What awaits is not just a bed, but an experience.'
            : 'Memesan mudah. Yang menanti bukan hanya kasur, tapi sebuah pengalaman.'}
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link to="/rooms"
            className="px-8 py-4 text-white font-sans font-bold rounded-xl transition-all hover:-translate-y-1 shadow-lg flex items-center gap-2"
            style={{ backgroundColor: wood.honey }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = wood.mid}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = wood.honey}>
            {t(copy.cta_rooms)} <ArrowRight size={16} />
          </Link>
          <Link to="/contact"
            className="px-8 py-4 font-sans font-bold rounded-xl transition-all hover:-translate-y-1 shadow-sm"
            style={{ backgroundColor: 'white', border: `2px solid ${wood.light}`, color: wood.dark }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = wood.cream}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
            {t(copy.cta_contact)}
          </Link>
        </div>
      </section>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CONTACT PAGE — konsisten dengan About (palet kayu elegan)
═══════════════════════════════════════════════════════════════ */
export function ContactPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  usePageTitle(lang === 'id' ? 'Kontak & Lokasi – Ndalem Mataram' : lang === 'zh' ? '联系与位置 – Ndalem Mataram' : 'Contact & Location – Ndalem Mataram');

  const t = (obj) => (typeof obj === 'object' ? obj[lang] || obj['id'] : obj);

  const wood = {
    dark:    '#2C1A0E',
    mid:     '#5C3D1E',
    honey:   '#8B5E3C',
    light:   '#C4956A',
    gold:    '#B8873F',
    cream:   '#F7F0E6',
    creamMid:'#EDE0CC',
  };

  const INFO = [
    {
      icon: <MapPin size={20} />,
      label: { id: 'Alamat Properti',  en: 'Property Address',   zh: '酒店地址'      },
      value: 'Jl. K.H. Agus Salim No.18,\nTaman Baru, Banyuwangi\nJawa Timur 68416',
      link:  'https://maps.google.com/?q=Ndalem+Mataram+Banyuwangi',
      linkLabel: { id: 'Buka di Google Maps', en: 'Open in Google Maps', zh: '在 Google 地图中打开' },
      accent: { bg: '#FDF6EC', border: '#E8D5B7', icon: wood.gold },
    },
    {
      icon: <Phone size={20} />,
      label: { id: 'WhatsApp Hotline', en: 'WhatsApp Hotline',   zh: 'WhatsApp 热线'  },
      value: '+62 813-7857-5758',
      link: (() => {
        const ph = '6281378575758';
        const admin = `\n\n====================\n*Pesan Otomatis untuk Admin:*\nTamu berbahasa asing ingin menanyakan reservasi melalui halaman kontak.`;
        const msg = lang === 'id'
          ? 'Halo Ndalem Mataram,\n\nSaya ingin menanyakan informasi mengenai reservasi kamar. Mohon bantuannya.\n\nTerima kasih.'
          : lang === 'zh'
          ? '你好 Ndalem Mataram，\n\n我想咨询有关客房预订的信息。请协助，谢谢。' + admin
          : 'Hello Ndalem Mataram,\n\nI would like to inquire about room availability and reservations. Could you assist me?\n\nThank you.' + admin;
        return `https://wa.me/${ph}?text=${encodeURIComponent(msg)}`;
      })(),
      linkLabel: { id: 'Chat Sekarang', en: 'Chat Now', zh: '立即联系' },
      accent: { bg: '#F0FDF4', border: '#BBF7D0', icon: '#16A34A' },
    },
    {
      icon: <Mail size={20} />,
      label: { id: 'Email Resmi',       en: 'Official Email',     zh: '官方邮箱'       },
      value: 'ndalem.mataram1@gmail.com',
      link:  'mailto:ndalem.mataram1@gmail.com',
      linkLabel: { id: 'Kirim Email', en: 'Send Email', zh: '发送邮件' },
      accent: { bg: '#EFF6FF', border: '#BFDBFE', icon: '#2563EB' },
    },
    {
      icon: <Clock size={20} />,
      label: { id: 'Jam Operasional',   en: 'Operating Hours',    zh: '营业时间'       },
      value: lang === 'zh' ? '前台：24小时 / 7天\n入住：14:00  |  退房：12:00'
           : lang === 'en' ? 'Front Desk: 24 Hours / 7 Days\nCheck-in: 14:00  |  Check-out: 12:00'
           : 'Resepsionis: 24 Jam / 7 Hari\nCheck-in: 14:00  |  Check-out: 12:00',
      link: null, linkLabel: null,
      accent: { bg: '#F8FAFC', border: '#E2E8F0', icon: '#475569' },
    },
  ];

 // ==========================================
  // DATA SECTION: MENUJU KAMI (HOW TO GET THERE)
  // ==========================================
  const HOWTOGET = lang === 'zh' ? [
    { icon: '', from: '从外南梦机场 (BWX)', dist: '± 12 公里', time: '乘出租车约20分钟', link: 'https://www.google.com/maps/dir/Banyuwangi+Airport/Ndalem+Mataram,+Banyuwangi' },
    { icon: '', from: '从 Banyuwangi Kota 火车站', dist: '± 3.3 公里', time: '乘网约车约7分钟', link: 'https://www.google.com/maps/dir/Stasiun+Banyuwangi+Kota/Ndalem+Mataram,+Banyuwangi' },
    { icon: '', from: '从 Ketapang 渡轮港口', dist: '± 10 公里', time: '直达市中心约18分钟', link: 'https://www.google.com/maps/dir/Pelabuhan+Ketapang/Ndalem+Mataram,+Banyuwangi' },
    { icon: '', from: '周边区域', dist: '市中心', time: '邻近当地美食区及便利店', link: 'https://www.google.com/maps/dir/Indomaret+Agus+Salim,+Jl.+K.H.+Agus+Salim+No.19,+Kebalenan,+Kec.+Banyuwangi,+Kabupaten+Banyuwangi,+Jawa+Timur+68417/Ndalem+Mataram,+Jl.+K.H.+Agus+Salim+No.18,+Taman+Baru,+Banyuwangi+Sub-District,+Banyuwangi+Regency,+East+Java+68416/@-8.2281638,114.3595993,17z/data=!3m1!4b1!4m14!4m13!1m5!1m1!1s0x2dd15ac90b974e49:0xec3be5e44c91d726!2m2!1d114.3611955!2d-8.2284011!1m5!1m1!1s0x2dd15b915a5ad2e9:0x75025f0abe471461!2m2!1d114.3612925!2d-8.2278818!3e0?entry=ttu&g_ep=EgoyMDI2MDYyMy4wIKXMDSoASAFQAw%3D%3D' },
  ] : lang === 'en' ? [
    { icon: '', from: 'From Banyuwangi Airport (BWX)', dist: '± 12 km', time: '20 minutes by taxi', link: 'https://www.google.com/maps/dir/Banyuwangi+Airport/Ndalem+Mataram,+Banyuwangi' },
    { icon: '', from: 'From Banyuwangi Kota Train Station', dist: '± 3.3 km', time: '7 minutes via ride-hailing', link: 'https://www.google.com/maps/dir/Stasiun+Banyuwangi+Kota/Ndalem+Mataram,+Banyuwangi' },
    { icon: '', from: 'From Ketapang Ferry Port', dist: '± 10 km', time: '18 minutes direct to city center', link: 'https://www.google.com/maps/dir/Pelabuhan+Ketapang/Ndalem+Mataram,+Banyuwangi' },
    { icon: '', from: 'Surrounding Area', dist: 'City Center', time: 'Near culinary spots & shops', link: 'https://www.google.com/maps/dir/Indomaret+Agus+Salim,+Jl.+K.H.+Agus+Salim+No.19,+Kebalenan,+Kec.+Banyuwangi,+Kabupaten+Banyuwangi,+Jawa+Timur+68417/Ndalem+Mataram,+Jl.+K.H.+Agus+Salim+No.18,+Taman+Baru,+Banyuwangi+Sub-District,+Banyuwangi+Regency,+East+Java+68416/@-8.2281638,114.3595993,17z/data=!3m1!4b1!4m14!4m13!1m5!1m1!1s0x2dd15ac90b974e49:0xec3be5e44c91d726!2m2!1d114.3611955!2d-8.2284011!1m5!1m1!1s0x2dd15b915a5ad2e9:0x75025f0abe471461!2m2!1d114.3612925!2d-8.2278818!3e0?entry=ttu&g_ep=EgoyMDI2MDYyMy4wIKXMDSoASAFQAw%3D%3D' },
  ] : [
    { icon: '', from: 'Dari Bandara Banyuwangi (BWX)', dist: '± 12 km', time: '20 menit berkendara taksi', link: 'https://www.google.com/maps/dir/Banyuwangi+Airport/Ndalem+Mataram,+Banyuwangi' },
    { icon: '', from: 'Dari Stasiun Banyuwangi Kota', dist: '± 3.3 km', time: '7 menit menggunakan ojek online', link: 'https://www.google.com/maps/dir/Stasiun+Banyuwangi+Kota/Ndalem+Mataram,+Banyuwangi' },
    { icon: '', from: 'Dari Pelabuhan Ketapang', dist: '± 10 km', time: '18 menit rute lurus ke pusat kota', link: 'https://www.google.com/maps/dir/Pelabuhan+Ketapang/Ndalem+Mataram,+Banyuwangi' },
    { icon: '', from: 'Area Sekitar', dist: 'Pusat Kota', time: 'Dekat kuliner Osing & minimarket', link: 'https://www.google.com/maps/dir/Indomaret+Agus+Salim,+Jl.+K.H.+Agus+Salim+No.19,+Kebalenan,+Kec.+Banyuwangi,+Kabupaten+Banyuwangi,+Jawa+Timur+68417/Ndalem+Mataram,+Jl.+K.H.+Agus+Salim+No.18,+Taman+Baru,+Banyuwangi+Sub-District,+Banyuwangi+Regency,+East+Java+68416/@-8.2281638,114.3595993,17z/data=!3m1!4b1!4m14!4m13!1m5!1m1!1s0x2dd15ac90b974e49:0xec3be5e44c91d726!2m2!1d114.3611955!2d-8.2284011!1m5!1m1!1s0x2dd15b915a5ad2e9:0x75025f0abe471461!2m2!1d114.3612925!2d-8.2278818!3e0?entry=ttu&g_ep=EgoyMDI2MDYyMy4wIKXMDSoASAFQAw%3D%3D' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: wood.cream }}>

      {/* ── HERO ── */}
      <div className="pt-36 pb-24 px-6 relative overflow-hidden" style={{ backgroundColor: wood.dark }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23C4956A'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")`
        }} />
        <div className="absolute bottom-0 left-0 right-0 h-24" style={{ background: `linear-gradient(to top, ${wood.dark}, transparent)` }} />
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <p className="text-xs font-sans tracking-[0.35em] uppercase mb-5 font-bold" style={{ color: wood.light }}>
            — {lang === 'zh' ? '联系与位置' : lang === 'en' ? 'Get in Touch' : 'Hubungi Kami'} —
          </p>
          <h1 className="font-display text-5xl lg:text-6xl text-white font-bold mb-6 leading-tight">
            {lang === 'zh' ? '联系 & Lokasi' : lang === 'en' ? 'Contact & Location' : 'Kontak & Lokasi'}
          </h1>
          <p className="font-sans text-lg max-w-2xl mx-auto leading-relaxed italic" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {lang === 'zh' ? '无论您有什么问题，我们都随时准备好为您解答。'
              : lang === 'en' ? 'Whatever your question, our team is always ready to help.'
              : 'Apapun pertanyaanmu, tim kami selalu siap membantu.'}
          </p>
        </div>
      </div>

      {/* ── KONTAK + MAPS ── */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">

          {/* Kolom kiri: 4 kartu */}
          <div className="space-y-4">
            <p className="text-xs font-sans tracking-[0.3em] uppercase font-bold mb-6" style={{ color: wood.gold }}>
              — {lang === 'zh' ? '联系方式' : lang === 'en' ? 'Reach Us' : 'Cara Menghubungi'} —
            </p>
            {INFO.map((item, i) => (
              <div key={i} className="group bg-white rounded-2xl p-6 flex gap-5 items-start transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                style={{ border: '1px solid #E8D5B7' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-300"
                  style={{ backgroundColor: item.accent.bg, border: `1px solid ${item.accent.border}`, color: item.accent.icon }}>
                  {item.icon}
                </div>
                <div className="space-y-1 flex-1">
                  <h3 className="font-sans font-bold text-sm uppercase tracking-wider" style={{ color: wood.dark }}>
                    {t(item.label)}
                  </h3>
                  <p className="font-sans text-sm leading-relaxed whitespace-pre-line" style={{ color: wood.honey }}>
                    {item.value}
                  </p>
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-bold pt-1 transition-colors"
                      style={{ color: wood.gold }}
                      onMouseEnter={e => e.currentTarget.style.color = wood.mid}
                      onMouseLeave={e => e.currentTarget.style.color = wood.gold}>
                      {t(item.linkLabel)} <ArrowRight size={11} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Kolom kanan: Google Maps */}
          <div>
            <p className="text-xs font-sans tracking-[0.3em] uppercase font-bold mb-6" style={{ color: wood.gold }}>
              — {lang === 'zh' ? '位置' : lang === 'en' ? 'Find Us' : 'Lokasi Kami'} —
            </p>
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl" style={{ border: `2px solid ${wood.light}` }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3948.749948689934!2d114.35871757500966!3d-8.227881791804881!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2dd15b915a5ad2e9%3A0x75025f0abe471461!2sNdalem%20Mataram!5e0!3m2!1sid!2sid!4v1782344892985!5m2!1sid!2sid"
                width="100%" height="380"
                style={{ border: 0, display: 'block' }}
                allowFullScreen loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ndalem Mataram Google Maps" />
              <div className="px-6 py-4 flex items-center justify-between" style={{ borderTop: `1px solid ${wood.light}40` }}>
                <div>
                  <p className="font-sans font-bold text-sm" style={{ color: wood.dark }}>Ndalem Mataram</p>
                  <p className="font-sans text-xs" style={{ color: wood.honey }}>Banyuwangi, Jawa Timur</p>
                </div>
                <a href="https://maps.google.com/?q=Ndalem+Mataram+Banyuwangi"
                  target="_blank" rel="noopener noreferrer"
                  className="text-xs font-bold flex items-center gap-1 transition-colors"
                  style={{ color: wood.gold }}
                  onMouseEnter={e => e.currentTarget.style.color = wood.mid}
                  onMouseLeave={e => e.currentTarget.style.color = wood.gold}>
                  {lang === 'zh' ? '获取路线' : lang === 'en' ? 'Get Directions' : 'Dapatkan Rute'} <ArrowRight size={11} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CARA MENUJU LOKASI ── */}
      <section className="py-20 px-6" style={{ backgroundColor: wood.creamMid }}>
        <div className="mt-16 mb-12 w-full">
          
          {/* Judul & Subjudul Section */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-serif font-bold mb-3" style={{ color: wood.dark }}>
              {lang === 'id' ? 'Menuju Ndalem Mataram' : lang === 'zh' ? '如何到达 Ndalem Mataram' : 'Getting to Ndalem Mataram'}
            </h2>
            <p className="font-sans text-sm max-w-2xl mx-auto" style={{ color: wood.honey }}>
              {lang === 'id' ? 'Pilih titik keberangkatan Anda dan klik kotak di bawah ini untuk melihat rute langsung di Google Maps.' 
              : lang === 'zh' ? '选择您的出发点，点击下方的方块直接在谷歌地图中查看路线。' 
              : 'Choose your starting point and click the boxes below to see the direct route on Google Maps.'}
            </p>
          </div>

          {/* Grid 4 Kotak Interaktif */}
          <div className="grid md:grid-cols-4 gap-4">
            {HOWTOGET.map((item, i) => (
              <a 
                key={i} 
                href={item.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 text-center block transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-pointer group"
                style={{ border: `1px solid ${wood.light}40`, textDecoration: 'none' }}
              >
                {/* Ikon */}
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                
                {/* Info Jarak & Waktu */}
                <p className="font-sans font-bold text-sm mb-1" style={{ color: wood.dark, margin: 0 }}>
                  {item.from}
                </p>
                <p className="font-sans text-xs font-semibold mb-2" style={{ color: wood.gold, margin: 0 }}>
                  {item.dist}
                </p>
                <p className="font-sans text-xs" style={{ color: wood.honey, margin: 0 }}>
                  {item.time}
                </p>
                
                {/* Garis & Teks 'Buka Google Maps' saat di-hover */}
                <div className="mt-4 pt-4 opacity-0 group-hover:opacity-100 transition-all duration-300 border-t" style={{ borderColor: `${wood.light}30` }}>
                  <p className="font-sans text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1" style={{ color: wood.dark, margin: 0 }}>
                    {lang === 'id' ? 'Buka Google Maps' : lang === 'zh' ? '打开谷歌地图' : 'Open Google Maps'}
                    <span style={{ color: wood.gold }}>↗</span>
                  </p>
                </div>
              </a>
            ))}
          </div>

        </div>
      </section>
    </div>
  );
}

/* ─── REVIEWS PAGE ─────────────────────────────────────────── */
export function ReviewsPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  usePageTitle(lang === 'id' ? 'Ulasan – Ndalem Mataram' : lang === 'zh' ? '评价 – Ndalem Mataram' : 'Reviews – Ndalem Mataram');

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#F7F0E6' }}>
      <PageHero 
        tag={lang === 'id' ? 'Ulasan' : lang === 'zh' ? '评价' : 'Reviews'}
        title={lang === 'id' ? 'Ulasan Tamu' : lang === 'zh' ? '客人评价' : 'Guest Reviews'}
        subtitle={lang === 'id' ? 'Apa kata mereka tentang Ndalem Mataram' : lang === 'zh' ? '客人们对 Ndalem Mataram 的评价' : 'What they say about Ndalem Mataram'} 
      />
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
         <p className="font-sans text-lg italic" style={{ color: '#8B5E3C' }}>
          {lang === 'id' ? 'Halaman ulasan sedang dalam tahap pembaruan.' : 
           lang === 'zh' ? '评论页面正在更新中。' : 
           'The reviews page is currently being updated.'}
        </p>
        <div className="mt-8">
          <Link to="/" className="font-sans text-sm font-semibold hover:underline" style={{ color: '#B8873F' }}>
            ← {lang === 'id' ? 'Kembali ke Beranda' : lang === 'zh' ? '返回首页' : 'Back to Home'}
          </Link>
        </div>
      </div>
    </div>
  );
}